<form class="form" [formGroup]="serviceRequestForm">
  <p-stepper [(value)]="currentStep">
    <p-step-list>
      <p-step [value]="1" i18n="@@sr_creation.step.base.title">Base information</p-step>
      <p-step
        [value]="2"
        [disabled]="
          shouldStepBeDisabled(['title', 'checkedWithColleagues', 'description', 'location'])
        "
        i18n="@@sr_creation.step.extended.title">
        Extended information
      </p-step>
      <p-step
        [value]="3"
        [disabled]="shouldStepBeDisabled(['title', 'description', 'severity', 'urgency'])"
        i18n="@@sr_creation.step.attachments.title">
        Attachments
      </p-step>
      <p-step
        [value]="4"
        [disabled]="shouldStepBeDisabled(['title', 'description', 'severity', 'urgency'])"
        i18n="@@sr_creation.step.summary.title">
        Summary
      </p-step>
    </p-step-list>
    <p-step-panels>
      <p-step-panel [value]="1">
        <ng-template #content>
          <h3 i18n="@@sr_creation.step.base.subtitle">Describe your issue</h3>
          <p i18n="@@sr_creation.step.base.intro">
            Please fill out the form below to help us quickly assess and assign the correct person
            to help you with your issue. Check with your department to see if a solution already
            exists.
          </p>
          <div class="form__field">
            <label for="title" class="input__label" i18n="@@sr_creation.field.title.label">
              Title
            </label>
            <input
              pInputText
              id="title"
              formControlName="title"
              placeholder="e.g. 'Laptop won't connect to VPN'"
              i18n-placeholder="@@sr_creation.field.title.placeholder" />
            @if (isFieldInvalid('title')) {
              <small class="input__error" i18n="@@sr_creation.field.title.required"
                >Title is required</small
              >
            }
          </div>
          <div class="form__row form__row--half">
            <div class="form__field">
              <label for="location" class="input__label" i18n="@@sr_creation.field.location.label">
                Location
              </label>
              <p-select
                id="location"
                formControlName="location"
                [options]="locationOptions"
                optionLabel="name"
                placeholder="Select a location"
                i18n-placeholder="@@sr_creation.field.location.placeholder">
              </p-select>
              @if (isFieldInvalid('location')) {
                <small class="input__error" i18n="@@sr_creation.field.location.required"
                  >Location is required</small
                >
              }
            </div>
            <div class="form__field">
              <label for="requestType" class="input__label" i18n="@@sr_creation.field.type.label">
                Type
              </label>
              <p-select
                id="requestType"
                formControlName="requestType"
                [options]="types"
                optionLabel="name"
                placeholder="Select a type"
                i18n-placeholder="@@sr_creation.field.type.placeholder">
              </p-select>
            </div>
          </div>
          <div class="form__field form__field--vertical-resizable">
            <label
              for="description"
              class="input__label"
              i18n="@@sr_creation.field.description.label">
              Description
            </label>
            <p-editor id="description" [modules]="editorModules" formControlName="description">
              <ng-template pTemplate="header"> </ng-template>
            </p-editor>
            <small class="input__hint" i18n="@@sr_creation.field.description.help">
              The better you can describe the issue you are having, the better we can help you
            </small>
            @if (isFieldInvalid('description')) {
              <small class="input__error" i18n="@@sr_creation.field.description.required"
                >Description is required</small
              >
            }
          </div>
          <div class="form__field form__field--horizontal">
            <p-toggleswitch id="confirm" formControlName="checkedWithColleagues" />
            <label
              class="input__label"
              [class.input__label--invalid]="isFieldInvalid('checkedWithColleagues')"
              for="confirm"
              i18n="@@sr_creation.field.checked.label">
              I've checked with colleagues and no solution is known
            </label>
          </div>
          <div class="stepper__actions">
            <p-button
              label="Next"
              i18n-label="@@common.button.next"
              icon="pi pi-arrow-right"
              iconPos="right"
              (onClick)="
                checkFieldsBeforeNextStep(
                  ['title', 'checkedWithColleagues', 'description', 'location'],
                  2
                )
              " />
          </div>
        </ng-template>
      </p-step-panel>

      <p-step-panel [value]="2">
        <ng-template #content>
          <h3 i18n="@@sr_creation.step.extended.subtitle.add_more">Add more info</h3>
          <div class="form__field">
            <label for="duedate" class="input__label" i18n="@@sr_creation.field.due_date.label"
              >Due date</label
            >
            <p-datepicker
              id="duedate"
              tooltipPosition="top"
              dateFormat="dd/mm/yy"
              placeholder="dd/MM/yyyy"
              [minDate]="today"
              formControlName="dueDate" />
            <small class="input__hint" i18n="@@sr_creation.field.due_date.help">
              Does the service request need to be fixed by a certain date?
            </small>
          </div>
          <h3 i18n="@@sr_creation.step.extended.subtitle.priority">Priority Factors</h3>
          <div class="form__row form__row--half">
            <div class="form__field form__field--horizontal">
              <p-toggleswitch id="compliance" formControlName="compliance" />
              <div>
                <label
                  for="compliance"
                  class="input__label"
                  i18n="@@sr_creation.field.compliance.label">
                  Compliance
                </label>
                <small class="input__hint" i18n="@@sr_creation.field.compliance.help">
                  Is compliance relevant to the service request?
                </small>
              </div>
            </div>
            <div class="form__field form__field--horizontal">
              <p-toggleswitch id="confidential" formControlName="confidential" />
              <div>
                <label
                  for="confidential"
                  class="input__label"
                  i18n="@@sr_creation.field.confidential.label">
                  Confidential
                </label>
                <small class="input__hint" i18n="@@sr_creation.field.confidential.help">
                  Does it involve sensitive data?
                </small>
              </div>
            </div>
          </div>
          <div class="form__row form__row--half">
            <div class="form__field">
              <label for="severity" class="input__label" i18n="@@sr_creation.field.severity.label">
                Severity
              </label>
              <p-select
                id="severity"
                placeholder="Severity"
                i18n-placeholder="@@sr_creation.field.severity.placeholder"
                formControlName="severity"
                optionLabel="name"
                [options]="severityOptions" />
              <small class="input__hint" i18n="@@sr_creation.field.severity.help">
                What is the impact scope?
              </small>
              @if (isFieldInvalid('severity')) {
                <small class="input__error" i18n="@@sr_creation.field.severity.required"
                  >Severity is required</small
                >
              }
            </div>
            <div class="form__field">
              <label for="urgency" class="input__label" i18n="@@sr_creation.field.urgency.label">
                Urgency
              </label>
              <p-select
                id="urgency"
                placeholder="Urgency"
                i18n-placeholder="@@sr_creation.field.urgency.placeholder"
                formControlName="urgency"
                optionLabel="name"
                [options]="urgencyOptions" />
              <small class="input__hint" i18n="@@sr_creation.field.urgency.help">
                How fast does it need response?
              </small>
              @if (isFieldInvalid('urgency')) {
                <small class="input__error" i18n="@@sr_creation.field.urgency.required"
                  >Urgency is required</small
                >
              }
            </div>
          </div>
          <h3 i18n="@@sr_creation.step.extended.subtitle.critical">Critical</h3>
          <div class="form__field form__field--horizontal form__field--warned">
            <p-toggleswitch id="critical" formControlName="critical" />
            <label class="input__label" for="critical" i18n="@@sr_creation.field.critical.help">
              Use this option when the issue completely prevents you from working or impacts a
              business-critical function!
            </label>
          </div>
          <div class="flex">
            <p class="col-6" i18n="@@sr_creation.field.pre_calculated_priority.label">
              Pre calculated priority: {{ serviceRequestPriority() }}
            </p>
          </div>
          <div class="stepper__actions">
            <p-button
              class="stepper__action--start"
              label="Back"
              i18n-label="@@common.button.back"
              severity="secondary"
              icon="pi pi-arrow-left"
              (onClick)="checkFieldsBeforeNextStep([], 1)" />
            <p-button
              label="Next"
              i18n-label="@@common.button.next"
              icon="pi pi-arrow-right"
              iconPos="right"
              (onClick)="checkFieldsBeforeNextStep(['severity', 'urgency'], 3)" />
          </div>
        </ng-template>
      </p-step-panel>

      <p-step-panel [value]="3">
        <ng-template #content>
          <h3 i18n="@@sr_creation.step.attachments.subtitle">Add files</h3>
          <div class="uploader">
            <small class="uploader__hint" i18n="@@sr_creation.field.attachments.help">
              You can upload images, excel, word files etc. to help us understand the issue better
            </small>
            <p-fileupload
              class="uploader__input"
              [ngClass]="{
                'uploader__input--loaded':
                  (serviceRequestForm.get('files')?.getRawValue()?.length ?? 0) > 0,
              }"
              name="attachments"
              [multiple]="true"
              accept="image/*, .pdf, .txt, .doc, .docx, .xls, .xlsx, .csv"
              mode="advanced"
              chooseIcon="pi pi-upload"
              chooseLabel="Choose"
              i18n-chooseLabel="@@common.button.choose"
              cancelLabel="Clear"
              i18n-cancelLabel="@@common.button.clear"
              showUploadButton="false"
              (onSelect)="onSelect($event)"
              (onClear)="onClear()">
              <ng-template #empty>
                <div i18n="@@sr_creation.field.attachments.label">
                  Drag and drop files here to upload.
                </div>
              </ng-template>
            </p-fileupload>
          </div>
          <div class="stepper__actions">
            <p-button
              class="stepper__action--start"
              label="Back"
              i18n-label="@@common.button.back"
              severity="secondary"
              icon="pi pi-arrow-left"
              (onClick)="checkFieldsBeforeNextStep([], 2)" />
            <p-button
              label="Next"
              i18n-label="@@common.button.next"
              icon="pi pi-arrow-right"
              iconPos="right"
              (onClick)="checkFieldsBeforeNextStep([], 4)" />
          </div>
        </ng-template>
      </p-step-panel>

      <p-step-panel [value]="4">
        <ng-template #content>
          <h3 i18n="@@sr_creation.step.summary.subtitle.confirm">Confirm and Submit</h3>
          <div class="summary">
            <label for="summary-title" class="summary__label" i18n="@@sr_creation.field.title.label"
              >Title</label
            >
            <p id="summary-title" class="summary__value">
              {{ serviceRequestForm.get('title')?.getRawValue() }}
            </p>
          </div>
          <div class="summary">
            <label for="summary-type" class="summary__label" i18n="@@sr_creation.field.type.label"
              >Type</label
            >
            <p id="summary-type" class="summary__value summary__value--capitalized">
              {{ serviceRequestForm.get('request')?.getRawValue() || 'not provided' }}
            </p>
          </div>
          <div class="summary">
            <label
              for="summary-description"
              class="summary__label"
              i18n="@@sr_creation.field.description.label"
              >Description</label
            >
            <p
              id="summary-description"
              class="summary__value"
              [innerHTML]="serviceRequestForm.get('description')?.getRawValue()"></p>
          </div>
          <h3 i18n="@@sr_creation.step.summary.subtitle.priority">Priority Factors</h3>
          <div class="summary">
            <label
              for="summary-duedate"
              class="summary__label"
              i18n="@@sr_creation.field.due_date.label"
              >Due date</label
            >
            <p id="summary-duedate" class="summary__value">
              <i class="pi pi-calendar summary__icon"></i>
              {{
                serviceRequestForm.get('dueDate')?.getRawValue()
                  ? (serviceRequestForm.get('dueDate')?.getRawValue() | date: 'dd/MM/yyyy')
                  : 'not provided'
              }}
            </p>
          </div>
          <div class="summary__row">
            <div class="summary col-6">
              <label
                for="summary-compliance"
                class="summary__label"
                i18n="@@sr_creation.field.compliance.label"
                >Compliance</label
              >
              <p
                id="summary-compliance"
                class="summary__value"
                [ngClass]="{
                  'summary__value--positive': serviceRequestForm.get('compliance')?.getRawValue(),
                  'summary__value--negative': !serviceRequestForm.get('compliance')?.getRawValue(),
                }">
                {{ serviceRequestForm.get('compliance')?.getRawValue() | yesNo }}
              </p>
            </div>
            <div class="summary col-6">
              <label
                for="summary-confidential"
                class="summary__label"
                i18n="@@sr_creation.field.confidential.label"
                >Confidential</label
              >
              <p
                id="summary-confidential"
                class="summary__value"
                [ngClass]="{
                  'summary__value--positive': serviceRequestForm.get('confidential')?.getRawValue(),
                  'summary__value--negative': !serviceRequestForm
                    .get('confidential')
                    ?.getRawValue(),
                }">
                {{ serviceRequestForm.get('confidential')?.getRawValue() | yesNo }}
              </p>
            </div>
          </div>
          <div class="summary__row">
            <div class="summary col-6">
              <label
                for="summary-severity"
                class="summary__label"
                i18n="@@sr_creation.field.severity.label"
                >Severity</label
              >
              <p id="summary-severity" class="summary__value">
                {{ serviceRequestForm.get('severity')?.getRawValue()?.name }}
              </p>
            </div>
            <div class="summary col-6">
              <label
                for="summary-urgency"
                class="summary__label"
                i18n="@@sr_creation.field.urgency.label"
                >Urgency</label
              >
              <p id="summary-urgency" class="summary__value">
                {{ serviceRequestForm.get('urgency')?.getRawValue()?.name }}
              </p>
            </div>
          </div>
          <div class="summary">
            <label
              for="summary-critical"
              class="summary__label"
              i18n="@@sr_creation.field.critical.label"
              >Critical</label
            >
            <p
              id="summary-critical"
              class="summary__value"
              [ngClass]="{
                'summary__value--positive': serviceRequestForm.get('critical')?.getRawValue(),
                'summary__value--negative': !serviceRequestForm.get('critical')?.getRawValue(),
              }">
              {{ serviceRequestForm.get('critical')?.getRawValue() | yesNo }}
            </p>
          </div>
          <div class="summary">
            <p class="summary__label" i18n="@@sr_creation.field.amount_of_files.label">
              Amount of attached files:
              {{ serviceRequestForm.get('files')?.getRawValue()?.length ?? 0 }}
            </p>
          </div>
          <div class="summary">
            <p class="summary__label" i18n="@@sr_creation.field.calculated_priority.label">
              Calculated priority:
              {{ serviceRequestPriority() | number: '1.0-0' }}
            </p>
          </div>
          <div class="stepper__actions">
            <p-button
              class="stepper__action--start"
              label="Back"
              i18n-label="@@common.button.back"
              severity="secondary"
              icon="pi pi-arrow-left"
              (onClick)="checkFieldsBeforeNextStep([], 3)" />
            <p-button
              label="Create service request"
              i18n-label="@@sr_creation.button.create_request"
              (click)="createServiceRequest()" />
          </div>
        </ng-template>
      </p-step-panel>
    </p-step-panels>
  </p-stepper>
</form>

<p-confirmdialog />
